import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { useCallback, useEffect, useState } from 'react';

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.card,
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    '&.Mui-expanded': {
      marginLeft: theme.spacing(1),
    },
    marginLeft: theme.spacing(1),
    alignItems: 'center',
  },
}));

type TButton = {
  name: string;
  value: any;
  displayName: string;
  isActive?: boolean;
};

interface AccordionSummaryButtonGroupProps {
  buttons: Array<TButton>;
  onClick: (index: number, item: any) => void;
  activeButtonValue?: string; // Optional prop for controlled active button state
  dotIds?: string[];
}

export const AccordionSummaryButtonGroup: React.FC<AccordionSummaryButtonGroupProps> = ({
  buttons,
  onClick,
  activeButtonValue,
  dotIds = [],
}) => {
  const [activeButton, setActiveButton] = useState<string | null>(activeButtonValue ?? null);

  // Sync internal state with the activeButtonIndex prop if provided
  useEffect(() => {
    if (activeButtonValue !== undefined) {
      setActiveButton(activeButtonValue);
    }
  }, [activeButtonValue]);

  const handleClick = useCallback(
    (index: number, button: TButton) => {
      if (activeButtonValue) {
        setActiveButton(activeButtonValue);
      }
      onClick(index, button);
    },
    [activeButtonValue, onClick],
  );

  return (
    <ButtonGroup
      variant="outlined"
      aria-label="button group"
      sx={(theme) => ({
        background: theme.palette.background.paperHighElevation,
        '& .MuiButtonGroup-lastButton': {
          borderTopLeftRadius: '4px!important',
          borderBottomLeftRadius: '4px!important',
        },
      })}
    >
      {buttons.map(({ name, value, displayName }, index: number) => (
        <Button
          key={index}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(index, { name, displayName, value });
          }}
          sx={(theme) => ({
            color:
              activeButton === value ? theme.palette.text.primary : theme.palette.text.secondary,
            backgroundColor: activeButton === value ? 'secondary.main' : 'transparent',
            border:
              activeButton === value
                ? `1px solid ${theme.palette.primary.light}`
                : '1px solid transparent',
            borderTopRightRadius: '4px!important',
            borderBottomRightRadius: '4px!important',
            borderRightColor:
              activeButton === value
                ? `${theme.palette.primary.light}!important`
                : 'transparent!important',
            '&:hover': {
              borderTopRightRadius: '4px!important',
              borderBottomRightRadius: '4px!important',
              borderRightColor: `${theme.palette.primary.light}!important`,
            },
          })}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {displayName}
            {dotIds.includes(value) && (
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#23CF4E',
                  marginLeft: 4,
                }}
              />
            )}
          </span>
        </Button>
      ))}
    </ButtonGroup>
  );
};

export default AccordionSummary;
